import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Edit, X } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Textarea } from "./ui/textarea";
import {
  OverdueDeliverySite,
  SiteRemark,
  addSiteRemark,
  getOverdueDeliverySites,
  getSiteRemarks,
  updateSiteRemark,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";

interface OverdueSiteRemarksDrawerProps {
  siteId: string | null;
  onClose: () => void;
}

const formatOverdueSince = (value: string) => {
  if (!value || value === "—") return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return value;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return format(date, "MMM dd, yyyy");
};

const OverdueSiteRemarksDrawer: React.FC<OverdueSiteRemarksDrawerProps> = ({
  siteId,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [remarkText, setRemarkText] = useState("");
  const [isAddingRemark, setIsAddingRemark] = useState(false);
  const [editingRemarkId, setEditingRemarkId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [isUpdatingRemark, setIsUpdatingRemark] = useState(false);

  const overdueSitesQuery = useQuery({
    queryKey: ["sites", "overdue"],
    queryFn: async () => getOverdueDeliverySites(),
    enabled: !!siteId,
  });

  const remarksQuery = useQuery({
    queryKey: ["sites", siteId, "remarks"],
    queryFn: async () => getSiteRemarks(siteId!),
    enabled: !!siteId,
  });

  useEffect(() => {
    if (!siteId) {
      setRemarkText("");
      setEditingRemarkId(null);
      setEditingText("");
    }
  }, [siteId]);

  useEffect(() => {
    if (!remarksQuery.error) return;
    toast.error(
      getBackendErrorMessage(remarksQuery.error, "Failed to load remarks"),
    );
  }, [remarksQuery.error]);

  const site: OverdueDeliverySite | null =
    overdueSitesQuery.data?.find((entry) => entry.id === siteId) ?? null;
  const remarks = remarksQuery.data ?? site?.remarks ?? [];

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["sites", "overdue"] }),
      queryClient.invalidateQueries({ queryKey: ["sites", siteId, "remarks"] }),
    ]);
  };

  const handleAddRemark = async () => {
    if (!siteId || !remarkText.trim()) {
      toast.error("Remark text is required");
      return;
    }
    setIsAddingRemark(true);
    try {
      await addSiteRemark(siteId, remarkText.trim());
      setRemarkText("");
      toast.success("Remark added");
      await refresh();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to add remark"));
    } finally {
      setIsAddingRemark(false);
    }
  };

  const startEditing = (remark: SiteRemark) => {
    setEditingRemarkId(remark.id);
    setEditingText(remark.text);
  };

  const cancelEditing = () => {
    setEditingRemarkId(null);
    setEditingText("");
  };

  const handleUpdateRemark = async () => {
    if (!siteId || !editingRemarkId || !editingText.trim()) {
      toast.error("Remark text is required");
      return;
    }
    setIsUpdatingRemark(true);
    try {
      await updateSiteRemark(siteId, editingRemarkId, editingText.trim());
      cancelEditing();
      toast.success("Remark updated");
      await refresh();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to update remark"));
    } finally {
      setIsUpdatingRemark(false);
    }
  };

  const addRemarkTextarea = (
    <Textarea
      value={remarkText}
      onChange={(e) => setRemarkText(e.target.value)}
      placeholder="Add a follow-up remark..."
      rows={isMobile ? 2 : 3}
    />
  );

  return (
    <SidePanel open={!!siteId} onClose={onClose} width="w-[720px]">
      {siteId && (
        <div className="relative h-full flex flex-col">
          {isMobile ? (
            <PanelHeader
              title="Overdue Site Remarks"
              subtitle={site?.name}
              onClose={onClose}
            />
          ) : (
            <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-[22px] font-semibold text-foreground font-jakarta">
                    Overdue Site Remarks
                  </h2>
                  {site && (
                    <p className="text-sm text-gray-500 mt-1 truncate">
                      {site.name}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0 flex-shrink-0"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <ScrollArea className="flex-1">
            <div
              className={
                isMobile ? "p-4 pb-8 space-y-6" : "px-6 py-4 space-y-6"
              }
            >
              {overdueSitesQuery.isLoading || remarksQuery.isLoading ? (
                <p className="text-sm text-gray-500">Loading site...</p>
              ) : site ? (
                <>
                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-2">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Site
                      </p>
                      <p className="text-sm font-medium mt-1">{site.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Customer
                      </p>
                      <p className="text-sm font-medium mt-1">
                        {site.customer?.fullName ?? "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Phone
                      </p>
                      <p className="text-sm font-medium mt-1">
                        {site.customer?.phoneNumber ?? "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Overdue since
                      </p>
                      <p className="text-sm font-medium mt-1">
                        {formatOverdueSince(site.overdueSince)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground">
                      Remarks
                    </h3>
                    {remarks.length === 0 ? (
                      <p className="text-sm text-gray-500">No remarks yet.</p>
                    ) : (
                      <ul className="space-y-3">
                        {remarks.map((remark) => (
                          <li
                            key={remark.id}
                            className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                          >
                            <div className="flex items-start justify-between gap-2 text-xs text-gray-500">
                              <div>
                                <span className="font-medium text-gray-700">
                                  {remark.user?.fullName ?? "Unknown user"}
                                </span>
                                <span className="ml-2">
                                  {format(
                                    new Date(remark.createdAt),
                                    "MMM dd, yyyy HH:mm",
                                  )}
                                </span>
                              </div>
                              {editingRemarkId !== remark.id && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-gray-600"
                                  onClick={() => startEditing(remark)}
                                >
                                  <Edit className="h-3.5 w-3.5 mr-1" />
                                  Edit
                                </Button>
                              )}
                            </div>
                            {editingRemarkId === remark.id ? (
                              <div className="mt-2 space-y-2">
                                <Textarea
                                  value={editingText}
                                  onChange={(e) =>
                                    setEditingText(e.target.value)
                                  }
                                  rows={3}
                                />
                                <div className="flex justify-end gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={isUpdatingRemark}
                                    onClick={cancelEditing}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={
                                      isUpdatingRemark || !editingText.trim()
                                    }
                                    onClick={() => void handleUpdateRemark()}
                                  >
                                    {isUpdatingRemark ? "Saving..." : "Save"}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm mt-2 whitespace-pre-wrap">
                                {remark.text}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {!isMobile && (
                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        {addRemarkTextarea}
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            disabled={isAddingRemark || !remarkText.trim()}
                            onClick={() => void handleAddRemark()}
                          >
                            {isAddingRemark ? "Adding..." : "Add remark"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">Site not found.</p>
              )}
            </div>
          </ScrollArea>
          {isMobile && site && (
            <div className="shrink-0 space-y-2 border-t border-gray-100 bg-white px-4 py-3">
              {addRemarkTextarea}
              <Button
                disabled={isAddingRemark || !remarkText.trim()}
                className="h-12 w-full rounded-2xl text-[15px] font-semibold"
                onClick={() => void handleAddRemark()}
              >
                {isAddingRemark ? "Adding…" : "Add remark"}
              </Button>
            </div>
          )}
        </div>
      )}
    </SidePanel>
  );
};

export default OverdueSiteRemarksDrawer;
